import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { validateAdminToken } from './api';

// Import components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import AdminNavbar from './admin/AdminNavbar';
import AdminFooter from './admin/AdminFooter';
import AdminLogin from './admin/AdminLogin';
import ErrorPage from './pages/ErrorPage';  // ADD THIS IMPORT
import WhatsAppButton from './components/WhatsAppButton';
import ToastContainer from './components/ToastContainer';

// Lazy load components
const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const Projects = React.lazy(() => import('./pages/Projects'));
const ProjectDetails = React.lazy(() => import('./pages/ProjectDetails'));
const Contact = React.lazy(() => import('./pages/Contact'));
const PropertyEnquiry = React.lazy(() => import('./pages/PropertyEnquiry'));

// Lazy load admin components
const AdminDashboard = React.lazy(() => import('./admin/AdminDashboard'));
const AdminManageProperties = React.lazy(() => import('./admin/AdminManageProperties'));
const AdminManagePropertyDetails = React.lazy(() => import('./admin/AdminManagePropertyDetails'));
const AdminManageQueries = React.lazy(() => import('./admin/AdminManageQueries'));
const AdminChangePassword = React.lazy(() => import('./admin/AdminChangePassword'));

const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const AdminRoute = ({ children, isAdmin }) => {
  if (!isAdmin) {
    return <Navigate to="/sec-pd-mgmt/login" replace />;
  }
  return children;
};

const AdminLoginRoute = ({ isAdmin, setIsAdmin }) => {
  if (isAdmin) {
    return <Navigate to="/sec-pd-mgmt/dashboard" replace />;
  }
  return <AdminLogin setIsAdmin={setIsAdmin} />;
};

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldUseDarkMode = savedTheme ? savedTheme === 'dark' : systemPrefersDark;

    setDarkMode(shouldUseDarkMode);
    applyTheme(shouldUseDarkMode);

    const checkAuthStatus = async () => {
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        try {
          const isValid = await validateAdminToken();
          if (isValid) {
            setIsAdmin(true);
          } else {
            localStorage.removeItem('adminToken');
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Error validating token:', error);
          localStorage.removeItem('adminToken');
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setIsCheckingAuth(false);
    };

    checkAuthStatus();

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e) => {
      if (!localStorage.getItem('theme')) {
        setDarkMode(e.matches);
        applyTheme(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  const applyTheme = (isDark) => {
    const root = document.documentElement;
    const body = document.body;

    if (isDark) {
      root.setAttribute('data-theme', 'dark');
      body.classList.add('dark-theme');
      body.classList.remove('light-theme');
    } else {
      root.setAttribute('data-theme', 'light');
      body.classList.add('light-theme');
      body.classList.remove('dark-theme');
    }
  };

  const toggleTheme = () => {
    const newTheme = !darkMode;
    setDarkMode(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  if (isCheckingAuth) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <ScrollToTop />
      <div className={`App ${darkMode ? 'dark-theme' : 'light-theme'}`}>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Admin Login Route */}
            <Route
              path="/sec-pd-mgmt/login"
              element={<AdminLoginRoute isAdmin={isAdmin} setIsAdmin={setIsAdmin} />}
            />

            {/* Admin Root Redirect */}
            <Route
              path="/sec-pd-mgmt"
              element={
                isAdmin ? (
                  <Navigate to="/sec-pd-mgmt/dashboard" replace />
                ) : (
                  <Navigate to="/sec-pd-mgmt/login" replace />
                )
              }
            />

            {/* Protected Admin Routes */}
            <Route
              path="/sec-pd-mgmt/*"
              element={
                <AdminRoute isAdmin={isAdmin}>
                  <AdminNavbar darkMode={darkMode} toggleTheme={toggleTheme} setIsAdmin={setIsAdmin} />
                  <Routes>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="properties" element={<AdminManageProperties />} />
                    <Route path="property-details/:id" element={<AdminManagePropertyDetails />} />
                    <Route path="queries" element={<AdminManageQueries />} />
                    <Route path="change-password" element={<AdminChangePassword />} />
                    <Route path="*" element={<ErrorPage />} />
                  </Routes>
                  <AdminFooter />
                </AdminRoute>
              }
            />

            {/* User Routes */}
            <Route
              path="/*"
              element={
                <>
                  <Navbar darkMode={darkMode} toggleTheme={toggleTheme} />
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/project/:id" element={<ProjectDetails />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/enquiry/:projectId" element={<PropertyEnquiry />} />
                    <Route path="*" element={<ErrorPage />} />
                  </Routes>
                  <Footer />
                  <WhatsAppButton />
                  <ToastContainer />
                </>
              }
            />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
